import React from "react";
import { Database } from "lucide-react";
import ConfirmationModal from "./ConfirmationModal.jsx";
import DataManagerBusinessCopyPeriodFields from "./DataManagerBusinessCopyPeriodFields.jsx";
import { useToast } from "./ResultsPrimitives.jsx";
import { copyRailwayBusinessToLocal, fetchRailwaySyncAvailability } from "../lib/dataManagerApi.js";
import { clearPageDataCache } from "../hooks/usePageData.js";
import { formatBusinessCopyDuration, formatBusinessCopyPeriod, isBusinessCopyPeriodValid } from "../utils/businessCopyPeriod.js";
import { RAILWAY_BUSINESS_COPY_PROGRESS_STEPS, createToastProgress, createToastProgressReporter } from "../utils/toastProgress.js";

const BUTTON = "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[4px] border border-accent bg-accent px-3 py-1 text-sm font-semibold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:ring-2 focus-visible:ring-accent/30 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";

function summaryItems(result) {
  const counts = result?.counts || {};
  const preservesOtherDates = result?.copy_mode === "business_period_merge";
  const retainedReferences = Object.values(result?.retained_reference_counts || {}).reduce((total, count) => total + count, 0);
  return [
    { label: "Période copiée", value: formatBusinessCopyPeriod(result?.period) },
    { label: "Durée", value: formatBusinessCopyDuration(result?.duration_seconds) },
    { label: "Commandes liées hors période", value: String(result?.additional_orders_count || 0) },
    { label: "Vérification", value: result?.verified === true ? (preservesOtherDates ? "Période actualisée et historique conservé vérifiés" : "Contenu métier conforme à l’instantané Railway") : "Non vérifiée", tone: "success" },
    { label: "Instantané de production", value: result?.source_at ? new Date(result.source_at).toLocaleString("fr-CA") : "—" },
    { label: "Commandes locales après copie", value: `${counts.orders || 0} commandes · ${counts.order_deliveries || 0} livraisons` },
    { label: "Commandes actualisées", value: String(result?.copied_counts?.orders ?? counts.orders ?? 0) },
    { label: "Commandes locales conservées", value: String(result?.retained_counts?.orders || 0) },
    { label: "Dépendances locales conservées", value: String(result?.retained_dependency_orders_count || 0) },
    { label: "Fiches nécessaires à l’historique conservées", value: String(retainedReferences) },
    { label: "Camions repris de Railway", value: String(counts.trucks || 0) },
    { label: "Dispatch et Consolidation", value: `${counts.dispatch_days || 0} journées · ${counts.consolidation_sheets || 0} relevés` },
    { label: "Facturation", value: `${counts.invoices || 0} factures locales · ${result?.pdf_count || 0} PDF copiés` },
    { label: "Historique", value: preservesOtherDates
      ? "La période choisie, les données ayant changé de date et leurs dépendances sont actualisées. Le reste de l’historique local est conservé. Les référentiels sont actualisés au complet et les tâches d’envoi copiées restent inactives."
      : "Validations et historiques de la sélection conservés. Les dépendances nécessaires sont incluses et les tâches d’envoi copiées restent inactives.", multiline: true },
  ];
}

export default function LocalToolsBusinessCopyCard({ availability }) {
  const { showToast, updateToast } = useToast();
  const [dialog, setDialog] = React.useState(null);
  const [period, setPeriod] = React.useState({ start_date: "", end_date: null });
  const [defaults, setDefaults] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const pending = React.useRef(false);

  async function open() {
    if (pending.current) return;
    pending.current = true;
    setError("");
    try {
      const current = await fetchRailwaySyncAvailability();
      if (!current.available || !current.default_period) throw new Error(current.message || "La copie est indisponible.");
      setDefaults(current.default_period);
      setPeriod({ ...current.default_period });
      setDialog({ mode: "confirm" });
    } catch (failure) {
      setError(failure.message);
    } finally {
      pending.current = false;
    }
  }

  function close() {
    if (pending.current) return;
    if (dialog?.mode === "summary") window.location.reload();
    else { setDialog(null); setError(""); }
  }

  async function copy() {
    if (pending.current || !isBusinessCopyPeriodValid(period)) return;
    pending.current = true;
    setBusy(true);
    setError("");
    const progress = createToastProgress(RAILWAY_BUSINESS_COPY_PROGRESS_STEPS);
    const toastId = showToast({
      type: "loading", duration: null, progress,
      title: "Copie de production en cours",
      message: "Copie des commandes, historiques et PDF de la période, avec les référentiels complets.",
    });
    const reportProgress = createToastProgressReporter(progress, (nextProgress) => {
      updateToast(toastId, { progress: nextProgress });
    });
    try {
      const result = await copyRailwayBusinessToLocal({ ...period,
        onProgress: reportProgress,
      });
      reportProgress("refresh");
      clearPageDataCache();
      setDialog({ mode: "summary", result });
      reportProgress("refresh", "completed");
      reportProgress.finish();
      updateToast(toastId, {
        type: "success", duration: 5000,
        title: "Copie de production réussie",
        message: `Copie terminée et vérifiée en ${formatBusinessCopyDuration(result.duration_seconds)}. Fermez le résumé pour actualiser l’application locale.`,
      });
    } catch (failure) {
      reportProgress.finish({ status: "failed" });
      updateToast(toastId, {
        type: "error", duration: 8000,
        title: "Copie de production impossible", message: failure.message,
      });
      setError(failure.message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  const completed = dialog?.mode === "summary";
  return <article className="grid content-start gap-3 rounded-[4px] border border-slate-300 bg-white p-4">
    <h2 className="flex items-center gap-2 text-base font-semibold text-slate-950"><Database size={18} /> Données de production</h2>
    <p className="text-sm leading-5 text-slate-600">Copier les commandes et historiques d’une période, avec les référentiels complets et les PDF, depuis Railway vers ce poste.</p>
    <button className={`${BUTTON} w-fit`} disabled={!availability?.available || busy} onClick={open}>Copier les données de production vers local</button>
    {!availability?.available ? <p className="text-sm text-slate-600">{availability?.message}</p> : null}
    {error && !dialog ? <p role="alert" className="text-sm text-red-700">{error}</p> : null}
    {dialog ? <ConfirmationModal
      title={completed ? "Copie vérifiée" : "Copier les données de production vers local"}
      eyebrow={completed ? "Résumé" : "Copie de production"}
      description={completed ? "La copie a été vérifiée. Fermez ce résumé pour recharger l’application locale."
        : "La période choisie sera remplacée. Les changements de date et leurs dépendances seront repris automatiquement. Le reste de l’historique local sera conservé et les référentiels seront actualisés au complet."}
      confirmLabel={completed ? "Fermer et actualiser" : busy ? "Copie…" : "Copier"}
      cancelLabel={completed ? "Fermer" : "Annuler"}
      onConfirm={completed ? close : copy} onCancel={close} onClose={close}
      isBusy={busy} tone="neutral" errorMessage={error}
      confirmDisabled={!completed && !isBusinessCopyPeriodValid(period)}
      summaryDensity="compact" summaryLayout="stack"
      summaryItems={completed ? summaryItems(dialog.result) : [{ label: "Période", value: formatBusinessCopyPeriod(period) }]}
      impactItems={completed ? [] : ["Camions, remorques, chauffeurs, clients, produits et paramètres métier : copie complète.",
        "Validations, verrous, journées fermées et PDF sont repris avec leurs dépendances.",
        "La flotte reprend exactement les camions de Railway. Les sauvegardes des anciens Dispatch restent consultables avec leur contenu historique.",
        "Comptes, droits, préférences et paramètres techniques restent locaux. Aucun courriel envoyé."]}
    >
      {!completed ? <DataManagerBusinessCopyPeriodFields period={period} defaultPeriod={defaults} disabled={busy} onChange={setPeriod} /> : null}
    </ConfirmationModal> : null}
  </article>;
}
